import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { Box, ButtonBase, Typography } from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { useUser } from "@framework/provider-user";
import type { IUser } from "@framework/types";

import { useSpeed } from "../../../../../../context/speed-context";
import { BODE_FREQUENCY_AXIS_SCALE, BODE_FREQUENCY_SCALE } from "../../../multi-analysis/types";
import { createStableColorAssigner } from "../../../scheme/components/analytics/utils/line-colors";
import { formatMeasurementNumber } from "../../../scheme/utils/measurement-format";
import type { ISpeedScreenFormContentProps } from "../types";
import { BodeChart } from "./chart";
import { getBodeFrequencyFactor } from "./chart-utils";
import {
  BODE_FREQUENCY_HZ_UNIT_CODE,
  BODE_PHASE_UNIT_CODE,
  BODE_ROTATION_FREQUENCY_UNIT_CODE,
  buildBodeSeries,
  formatBodeHeader,
  formatBodeUnitLabel,
  getBodeFrameMarks,
  resolveBodeCursor,
} from "./model";

export const BodeScreenForm: FC<ISpeedScreenFormContentProps> = ({ windowId }) => {
  const { formatMessage } = useIntl();
  const { profile } = useUser<IUser>();
  const { selectedPoints, dataMarks, selectedDataMarks, frameRange, dataMarksError, isDataMarksLoading, toolbarState } =
    useSpeed();
  const colors = useRef(createStableColorAssigner());
  const [cursorKey, setCursorKey] = useState<string | null>(null);
  const [hiddenSeriesKeys, setHiddenSeriesKeys] = useState<string[]>([]);
  const hiddenSeries = useMemo(() => new Set(hiddenSeriesKeys), [hiddenSeriesKeys]);
  const toggleSeries = useCallback((key: string) => {
    setHiddenSeriesKeys(keys => (keys.includes(key) ? keys.filter(item => item !== key) : [...keys, key]));
  }, []);
  const marks = useMemo(() => getBodeFrameMarks(dataMarks, frameRange), [dataMarks, frameRange]);
  const series = useMemo(
    () =>
      buildBodeSeries(
        marks,
        unitCode => formatBodeUnitLabel(formatMessage({ id: `enums.units.${unitCode}` })),
        key => colors.current.get(key),
      ),
    [formatMessage, marks],
  );
  const selectedMarkKey = selectedDataMarks[selectedDataMarks.length - 1]?.key ?? null;
  useEffect(() => {
    if (selectedMarkKey) setCursorKey(selectedMarkKey);
  }, [selectedMarkKey]);
  const visibleSampleKeys = useMemo(
    () =>
      new Set(
        series.filter(item => !hiddenSeries.has(item.key)).flatMap(item => item.samples.map(sample => sample.key)),
      ),
    [hiddenSeries, series],
  );
  const cursor = resolveBodeCursor(
    marks.filter(mark => visibleSampleKeys.has(mark.key)),
    cursorKey,
  );
  const formatValue = useCallback(
    (value: number, unitCode: number | null) =>
      formatMeasurementNumber(value, unitCode, profile.interfaceSettings.dataFormat, { fallbackDigits: 2 }),
    [profile.interfaceSettings.dataFormat],
  );
  const frequencyInHertz = toolbarState.bodeFrequencyScale === BODE_FREQUENCY_SCALE.HERTZ;
  const header = cursor
    ? formatBodeHeader(
        cursor,
        formatBodeUnitLabel(formatMessage({ id: `enums.units.${cursor.units}` })),
        formatBodeUnitLabel(formatMessage({ id: `enums.units.${BODE_PHASE_UNIT_CODE}` })),
        frequencyInHertz
          ? formatMessage({ id: "pages.speed.bode.frequencyHertzUnit" })
          : formatBodeUnitLabel(formatMessage({ id: `enums.units.${BODE_ROTATION_FREQUENCY_UNIT_CODE}` })),
        formatValue,
        getBodeFrequencyFactor(toolbarState.bodeFrequencyScale),
        frequencyInHertz ? BODE_FREQUENCY_HZ_UNIT_CODE : BODE_ROTATION_FREQUENCY_UNIT_CODE,
      )
    : null;
  const placeholder = !selectedPoints.length
    ? "pages.multiAnalysis.chartPlaceholder"
    : dataMarksError
      ? "pages.speed.bode.loadError"
      : "pages.multiAnalysis.chartEmpty";

  return (
    <Box
      data-testid={`speed-bode-${windowId}`}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
      }}
    >
      {isDataMarksLoading ? <ProgressOverlay isLoading /> : null}
      {series.length ? (
        <>
          <Box sx={{ px: 1, py: 0.5, display: "flex", gap: 1.5, flexWrap: "wrap", flexShrink: 0 }}>
            {series.map(item => (
              <ButtonBase
                key={item.key}
                aria-pressed={!hiddenSeries.has(item.key)}
                onClick={() => toggleSeries(item.key)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  minWidth: 0,
                  borderRadius: 0.5,
                  px: 0.5,
                  py: 0.25,
                  opacity: hiddenSeries.has(item.key) ? 0.5 : 1,
                  "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 1 },
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.color, flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.secondary",
                    overflowWrap: "anywhere",
                    textDecoration: hiddenSeries.has(item.key) ? "line-through" : "none",
                  }}
                >
                  {item.name}
                </Typography>
              </ButtonBase>
            ))}
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: "grid", gridTemplateRows: "1fr 1fr" }}>
            {(["amplitude", "phase"] as const).map(kind => (
              <Box
                key={kind}
                sx={{
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  borderBottom: kind === "amplitude" ? 1 : 0,
                  borderColor: "divider",
                }}
              >
                <Typography
                  title={header ?? undefined}
                  aria-live="polite"
                  sx={{
                    px: 1,
                    py: 0.5,
                    fontSize: 12,
                    fontWeight: 500,
                    overflowWrap: "anywhere",
                    flexShrink: 0,
                  }}
                >
                  {header}
                </Typography>
                <BodeChart
                  kind={kind}
                  series={series}
                  cursorKey={cursor?.key ?? null}
                  onCursorChange={setCursorKey}
                  formatValue={formatValue}
                  frequencyScale={toolbarState.bodeFrequencyScale}
                  frequencyAxisFromZero={toolbarState.bodeFrequencyAxisScale === BODE_FREQUENCY_AXIS_SCALE.ZERO}
                  markerMode={toolbarState.markerMode}
                  hiddenSeriesKeys={hiddenSeriesKeys}
                />
              </Box>
            ))}
          </Box>
        </>
      ) : (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
          {!isDataMarksLoading ? (
            <Typography color={dataMarksError ? "error" : "textSecondary"} sx={{ fontSize: 13 }} align="center">
              {formatMessage({ id: placeholder })}
            </Typography>
          ) : null}
        </Box>
      )}
    </Box>
  );
};
